<template>
  <ion-app>
    <!-- Side drawers, both bound to the router outlet below via content-id so
         they overlay the whole app shell. Left = app/info (header logo);
         right = account (header avatar). -->
    <AppInfoMenu />
    <AppSideMenu />
    <!-- Connectivity strip. It occupies the top edge, so the outlet is pushed
         down by the same height while it's up (see .outlet--offline below). -->
    <OfflineBanner :show="showOfflineBanner" :blocked="isBlocked" :pending-count="syncStore.pendingCount" />
    <ion-router-outlet id="main-content" :class="{ 'outlet--offline': showOfflineBanner }" />
    <!-- Survey/consent popup — app-wide (covers the standalone authed pages
         outside /tabs too, so a required survey cannot be escaped by navigating). -->
    <SurveyPromptHost />
    <!-- PWA lifecycle: the "update available" banner and the one-time
         "install this app" promotion. Both are app-wide for the same reason. -->
    <UpdatePromptHost />
    <InstallPromptHost />
    <!-- Version watchdog: a newer build is deployed but the worker didn't (or
         couldn't) say so. Supersedes the banner above when both fire. -->
    <UpdateRequiredHost />
    <!-- The "Updating Syntry…" wall both prompts above hand over to, and the
         "Update complete" toast on the reloaded build. Teleported to <body>, so
         its place here does not affect stacking. -->
    <UpdateProgressHost />
    <!-- Event reminders — app-wide for the same reason as the hosts above: a
         reminder must reach the standalone authed pages (/leaves, /payslips,
         /events/…) and not just the tabs. Also owns the native-notification tap
         listener, which needs the router. -->
    <ReminderHost />
    <!-- Shared file viewer (images + PDFs) behind openFileViewer(). MUST stay
         LAST: Ionic hands hardware-back / Escape to the last overlay in document
         order, so this is what makes Back close the viewer instead of the modal
         or page beneath it — and the first overlay connected is skipped by that
         lookup entirely. -->
    <FileViewerHost />
  </ion-app>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import { IonApp, IonRouterOutlet } from '@ionic/vue';
import { useRoute, useRouter } from 'vue-router';
import AppInfoMenu from '@/components/layout/AppInfoMenu.vue';
import AppSideMenu from '@/components/layout/AppSideMenu.vue';
import SurveyPromptHost from '@/components/layout/SurveyPromptHost.vue';
import UpdatePromptHost from '@/components/layout/UpdatePromptHost.vue';
import InstallPromptHost from '@/components/layout/InstallPromptHost.vue';
import UpdateRequiredHost from '@/components/layout/UpdateRequiredHost.vue';
import UpdateProgressHost from '@/components/layout/UpdateProgressHost.vue';
import ReminderHost from '@/components/layout/ReminderHost.vue';
import FileViewerHost from '@/components/layout/FileViewerHost.vue';
import OfflineBanner from '@/components/layout/OfflineBanner.vue';
import { useSettingsStore } from '@/stores/settings.store';
import { useSessionStore } from '@/stores/session.store';
import { useSyncStore } from '@/stores/sync.store';
import { isBlocked, startConnectivityWatch } from '@/services/connectivity';
import { offlineBannerActive } from '@/pwa/offline-ui';
import { isPublicRouteName } from '@/router';
import { useDoubleTapTheme } from '@/composables/useDoubleTapTheme';
import { startRealtime, stopRealtime } from '@/services/realtime.service';
import { consumePendingRoute } from '@/services/realtime-navigation';
import { notificationsDevice } from '@/services/device/notifications.service';

// Double-tap empty space to flip light/dark. Registered once at the app root:
// it replaced the per-page theme button, so every screen needs it, not just the
// one it was prototyped on.
useDoubleTapTheme();

const settingsStore = useSettingsStore();
const sessionStore = useSessionStore();
// Punches saved on this device that are still waiting to sync — the offline
// banner counts them ("You're offline — 2 punches waiting to sync.").
const syncStore = useSyncStore();
const route = useRoute();
const router = useRouter();

// Sign-in and onboarding have their own connectivity affordance (the server
// status pill), so the global banner would only duplicate it there.
const showOfflineBanner = computed(
  () => offlineBannerActive.value && !isPublicRouteName(route.name),
);

// Realtime socket lifecycle. Runs only for an authenticated session on a
// non-public route: `restore()` / `applyTokenResponse()` flip `isAuthenticated`
// (start), sign-out or expiry flips it back (stop — `signOutLocal()` also stops
// it directly so no frame can land mid-teardown), and the public-route half
// covers the brief window in which a token exists but the guard has not yet
// bounced the user off sign-in/onboarding. Immediate so a restored session
// connects on boot without waiting for a navigation.
//
// This route-driven stop keeps the persisted cursor (`forget: false`): a hop
// through a public route is not a sign-out. The sign-out stop — the one that
// removes the cursor (DEC-083) — lives in `session.store.signOutLocal()`.
const realtimeWanted = computed(
  () => sessionStore.isAuthenticated && !isPublicRouteName(route.name),
);
watch(
  realtimeWanted,
  (wanted) => {
    if (wanted) startRealtime();
    else stopRealtime({ forget: false });
  },
  { immediate: true },
);

// The ONE consumer of a parked realtime target on the RESTORE path (a token
// already present at boot — a cold-start device-alert tap, a deep link, or the
// route the guard bounced off to sign-in): `consumePendingRoute()` drops an
// action that is stale or stamped for another tenant / user, so a foreign
// target never lands on a thread page. The login path's consumer is
// SignInPage (both success sites); this watcher fires ONLY for `restore()` —
// `restoring` is true just around that call, so a later login → authenticated
// flip does not consume a second time.
let restoring = false;
watch(
  () => sessionStore.isAuthenticated,
  (authenticated) => {
    if (!authenticated || !restoring) return;
    const target = consumePendingRoute();
    if (target) void router.replace(target).catch(() => undefined);
  },
  { flush: 'sync' },
);

onMounted(() => {
  settingsStore.initTheme();
  // Boot-level device wiring (phase6-contract.md §3.7): the native tap
  // listener, the push-plugin listeners and the foreground rechecks are
  // attached ONCE here — not at realtime start — so a cold-start tap on a
  // device alert is parked for the consumer above instead of lost. The
  // service worker's message subscription (§3.8) is push.service.ts's own,
  // taken inside this prepare(). Idempotent leaf.
  notificationsDevice.prepare();
  // Reflect any token the SDK restored from storage into session status.
  restoring = true;
  try {
    sessionStore.restore();
  } finally {
    restoring = false;
  }
  startConnectivityWatch();
});
</script>

<!-- Non-scoped: ion-router-outlet is a host element, so the offset must not be
     scoped away. -->
<style lang="scss">
ion-router-outlet#main-content.outlet--offline {
  top: calc(32px + var(--syntry-safe-top, env(safe-area-inset-top, 0px)));
  transition: top 200ms ease;
}

@media (prefers-reduced-motion: reduce) {
  ion-router-outlet#main-content.outlet--offline {
    transition: none;
  }
}
</style>
